import React, { useCallback, useState } from "react";
import { Box, Card, CardContent, CardHeader } from "@material-ui/core";
import { mergeDeepRight } from "ramda";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import Grid from "@material-ui/core/Grid";
import { isBlank, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import YesNoCard from "../../components/YesNoCard";
import { makeStyles } from "@material-ui/core/styles";
import ReadOnlyField from "../../components/ReadOnlyField";
import CustomFieldsDialog from "../edit/CustomFieldsDialog";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const CustomFieldsCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const handleEditClick = useCallback(() => {
    setEditDialogOpen(true);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setEditDialogOpen(false);
      const update = mergeDeepRight(project, {
        fieldOptions: {
          custom_freeform: {
            fieldLabel: data.customFreeformLabel,
            required: data.customFreeformRequired,
          },
        },
      });
      onUpdate(update);
    },
    [project, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Custom Fields"
        action={
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Tooltip title="Edit Custom Field Properties">
              <IconButton onClick={handleEditClick}>
                <EditIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Project Custom Field Properties.
          </Box>
        )}
        {notBlank(project) && (
          <Grid container spacing={3}>
            <Grid item sm={9}>
              <LabeledReadOnly label="Custom Field Label">
                <ReadOnlyField>
                  {project.fieldOptions["custom_freeform"]
                    ? project.fieldOptions["custom_freeform"].fieldLabel
                    : null}
                </ReadOnlyField>
              </LabeledReadOnly>
            </Grid>

            <Grid item sm={3}>
              <LabeledReadOnly label="Required">
                <YesNoCard
                  value={
                    project.fieldOptions["custom_freeform"] &&
                    project.fieldOptions["custom_freeform"].required
                  }
                />
              </LabeledReadOnly>
            </Grid>
          </Grid>
        )}
      </CardContent>
      {editDialogOpen && (
        <CustomFieldsDialog
          open={editDialogOpen}
          fieldOptions={project.fieldOptions}
          onCancel={handleEditCancel}
          onApply={handleDialogApply}
        />
      )}
    </Card>
  );
};

export default CustomFieldsCard;
